/**
 * Sound Service
 * Manages audio notifications for various chat events
 * Uses Web Audio API for cross-browser compatibility
 * Fetches server-side defaults and merges with user preferences
 */

import soundGenerator from '@/utils/soundGenerator';
import api from '@/services/api';

export type SoundType =
  | 'message'
  | 'mention'
  | 'join'
  | 'leave'
  | 'mute'
  | 'unmute'
  | 'notification'
  | 'ding';

interface SoundSettings {
  enabled: boolean;
  volume: number; // 0-100
  muteNotifications: boolean;
  muteVoiceEvents: boolean;
  silentMode: boolean; // Complete silence - overrides all settings
}

interface SoundSettingsStorage extends SoundSettings {
  customized: boolean; // Track if user has customized settings
}

class SoundService {
  private settings: SoundSettings;
  private isMuted = false;
  private serverDefaults: SoundSettings | null = null;
  private initialized = false;
  private isCustomized = false;

  constructor() {
    this.settings = this.getDefaultSettings();
  }

  /**
   * Initialize service - fetch server defaults and apply user settings
   */
  async initialize(): Promise<void> {
    if (this.initialized) return;

    try {
      // Always fetch server defaults
      const response = await api.get('/sounds/defaults');
      this.serverDefaults = response.data;

      // Load user settings from localStorage
      const userSettings = this.loadSettings();

      if (userSettings && userSettings.customized) {
        // User has customized - use their settings
        this.settings = userSettings;
        this.isCustomized = true;
      } else if (userSettings) {
        // User has localStorage but hasn't customized - use fresh server defaults
        this.settings = {
          enabled: this.serverDefaults?.enabled ?? true,
          volume: this.serverDefaults?.volume ?? 70,
          muteNotifications: this.serverDefaults?.muteNotifications ?? false,
          muteVoiceEvents: this.serverDefaults?.muteVoiceEvents ?? false,
          silentMode: userSettings.silentMode ?? false, // Preserve silent mode preference
        };
        this.isCustomized = false;
      } else {
        // First time user - use server defaults
        this.settings = {
          enabled: this.serverDefaults?.enabled ?? true,
          volume: this.serverDefaults?.volume ?? 70,
          muteNotifications: this.serverDefaults?.muteNotifications ?? false,
          muteVoiceEvents: this.serverDefaults?.muteVoiceEvents ?? false,
          silentMode: false,
        };
        this.isCustomized = false;
      }
    } catch (error) {
      console.warn('Failed to fetch server sound defaults:', error);
      // Fall back to local settings
      const userSettings = this.loadSettings();
      if (userSettings && userSettings.customized) {
        this.settings = userSettings;
        this.isCustomized = true;
      } else {
        this.settings = this.getDefaultSettings();
        this.isCustomized = false;
      }
    }

    this.initialized = true;
  }

  /**
   * Get default settings (hardcoded fallback)
   */
  private getDefaultSettings(): SoundSettings {
    return {
      enabled: true,
      volume: 70,
      muteNotifications: false,
      muteVoiceEvents: false,
      silentMode: false,
    };
  }

  /**
   * Play a sound using Web Audio API
   */
  play(type: SoundType) {
    // Silent mode - mute all sounds
    if (this.settings.silentMode || !this.settings.enabled || this.isMuted) {
      return;
    }

    // Check if this sound type should be muted
    if (
      (type === 'join' || type === 'leave' || type === 'mute' || type === 'unmute') &&
      this.settings.muteVoiceEvents
    ) {
      return;
    }

    if (
      (type === 'message' || type === 'mention' || type === 'notification') &&
      this.settings.muteNotifications
    ) {
      return;
    }

    try {
      // Use Web Audio API for sound generation with volume control
      soundGenerator.playSound(type);
    } catch (error) {
      console.warn(`Error playing sound ${type}:`, error);
    }
  }

  /**
   * Set volume (0-100)
   */
  setVolume(volume: number) {
    const clampedVolume = Math.max(0, Math.min(100, volume));
    this.settings.volume = clampedVolume;
    this.markAsCustomized();

    this.saveSettings();
  }

  /**
   * Get current volume
   */
  getVolume(): number {
    return this.settings.volume;
  }

  /**
   * Toggle sounds on/off
   */
  toggleSounds(enabled?: boolean) {
    this.settings.enabled = enabled !== undefined ? enabled : !this.settings.enabled;
    this.markAsCustomized();
    this.saveSettings();
    return this.settings.enabled;
  }

  /**
   * Check if sounds are enabled
   */
  areSoundsEnabled(): boolean {
    return this.settings.enabled;
  }

  /**
   * Toggle mute (temporarily mute all sounds)
   */
  toggleMute(muted?: boolean) {
    this.isMuted = muted !== undefined ? muted : !this.isMuted;
    return this.isMuted;
  }

  /**
   * Check if currently muted
   */
  isSoundMuted(): boolean {
    return this.isMuted;
  }

  /**
   * Toggle notification sounds
   */
  toggleNotificationSounds(enabled?: boolean) {
    this.settings.muteNotifications =
      enabled !== undefined ? !enabled : !this.settings.muteNotifications;
    this.markAsCustomized();
    this.saveSettings();
    return !this.settings.muteNotifications;
  }

  /**
   * Toggle voice event sounds
   */
  toggleVoiceEventSounds(enabled?: boolean) {
    this.settings.muteVoiceEvents =
      enabled !== undefined ? !enabled : !this.settings.muteVoiceEvents;
    this.markAsCustomized();
    this.saveSettings();
    return !this.settings.muteVoiceEvents;
  }

  /**
   * Toggle silent mode (complete silence)
   */
  toggleSilentMode(silent?: boolean) {
    this.settings.silentMode = silent !== undefined ? silent : !this.settings.silentMode;
    this.markAsCustomized();
    this.saveSettings();
    return this.settings.silentMode;
  }

  /**
   * Reset sounds to server defaults
   */
  resetToDefaults() {
    if (this.serverDefaults) {
      this.settings = {
        enabled: this.serverDefaults.enabled,
        volume: this.serverDefaults.volume,
        muteNotifications: this.serverDefaults.muteNotifications,
        muteVoiceEvents: this.serverDefaults.muteVoiceEvents,
        silentMode: false, // Always reset silent mode to off
      };
    } else {
      this.settings = this.getDefaultSettings();
    }
    this.isCustomized = false;
    this.saveSettings();
  }

  /**
   * Mark that user has customized settings
   */
  private markAsCustomized() {
    this.isCustomized = true;
  }

  /**
   * Get current settings
   */
  getSettings(): SoundSettings {
    return { ...this.settings };
  }

  /**
   * Load settings from localStorage
   */
  private loadSettings(): SoundSettingsStorage | null {
    try {
      const saved = localStorage.getItem('soundSettings');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.warn('Failed to load sound settings:', error);
    }

    return null;
  }

  /**
   * Save settings to localStorage
   */
  private saveSettings() {
    try {
      const toSave: SoundSettingsStorage = {
        ...this.settings,
        customized: this.isCustomized,
      };
      localStorage.setItem('soundSettings', JSON.stringify(toSave));
    } catch (error) {
      console.warn('Failed to save sound settings:', error);
    }
  }

  /**
   * Preload sounds (Web Audio API doesn't require preloading)
   */
  preloadSounds(): Promise<void> {
    // Web Audio API sounds generate on-demand, no preloading needed
    return Promise.resolve();
  }
}

export default new SoundService();
